/**
 * Convite do proprietário de uma empresa, na tela da Plataforma: situação, revogação do
 * convite pendente e emissão de um novo convite (empresa sem proprietário ativo).
 * O convite antigo nunca é alterado nem apagado: fica no histórico como revogado/expirado.
 * Quem vira proprietário é só quem ACEITA o convite pelo link (fluxo de sempre).
 */
import { issueOwnerInvitationSchema, type CompanyOwnerStatus } from "@cafegestor/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Alert, Badge, Button, Input } from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { OWNER_INVITATION_LABEL, ownerView } from "./owner-invitation";

type Issued = {
  invitationId: string;
  email: string;
  expiresAt: string;
  delivery: "sent" | "existing_account" | "disabled" | "failed" | null;
  acceptUrl: string | null;
};

const DELIVERY: Record<string, string> = {
  sent: "Novo convite enviado por e-mail.",
  existing_account:
    "Esta pessoa já tem conta: envie o link abaixo (ela entra com a conta que já tem).",
  disabled: "Envio de e-mail não configurado: copie o link abaixo e envie à pessoa.",
  failed:
    "O convite foi criado, mas o e-mail não foi enviado: copie o link abaixo e envie à pessoa.",
};

export function OwnerInvitationPanel({
  companyId,
  owner,
}: {
  companyId: string;
  owner: CompanyOwnerStatus;
}) {
  const queryClient = useQueryClient();
  const view = ownerView(owner);
  const [confirming, setConfirming] = useState(false);
  const [revokeKey, setRevokeKey] = useState(newIdempotencyKey);
  const [issueKey, setIssueKey] = useState(newIdempotencyKey);
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["plataforma"] });

  const revoke = useMutation({
    mutationFn: (invitationId: string) =>
      apiFetch<{ status: string }>(
        `/plataforma/empresas/${companyId}/convites-proprietario/${invitationId}/revogar`,
        { method: "POST", idempotencyKey: revokeKey, withCompany: false },
      ),
    onSuccess: async () => {
      setConfirming(false);
      setRevokeKey(newIdempotencyKey());
      await refresh();
    },
  });

  const issue = useMutation({
    mutationFn: (body: { email: string }) =>
      apiFetch<Issued>(`/plataforma/empresas/${companyId}/convites-proprietario`, {
        method: "POST",
        body,
        idempotencyKey: issueKey,
        withCompany: false,
      }),
    onSuccess: async () => {
      setEmail("");
      setIssueKey(newIdempotencyKey());
      await refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = issueOwnerInvitationSchema.safeParse({ email });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "E-mail inválido");
    setFormError(null);
    issue.mutate(parsed.data);
  }

  return (
    <div className="mt-1 space-y-2 text-sm">
      {view.kind === "owner" && (
        <div>
          <Badge tone="ok">Proprietário ativo</Badge>{" "}
          <span className="text-muted-foreground">{view.emails.join(", ")}</span>
        </div>
      )}

      {view.kind === "pending" && (
        <div className="space-y-2">
          <div>
            <Badge tone="warn">Convite de proprietário pendente</Badge> <span>{view.email}</span>{" "}
            <span className="text-muted-foreground">
              (vale até {new Date(view.expiresAt).toLocaleString("pt-BR")})
            </span>
          </div>
          {!confirming ? (
            <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
              Revogar convite
            </Button>
          ) : (
            <Alert tone="warn">
              Revogar o convite enviado para <strong>{view.email}</strong>? O link deixa de
              funcionar e o convite fica no histórico como revogado. Depois você poderá emitir um
              novo convite para o e-mail correto.
              <span className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(view.invitationId)}
                >
                  Confirmar revogação
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revoke.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Cancelar
                </Button>
              </span>
            </Alert>
          )}
          {revoke.isError && <Alert tone="error">{errorMessage(revoke.error)}</Alert>}
        </div>
      )}

      {view.kind === "issue" && (
        <div className="space-y-2">
          <div>
            <Badge tone="error">Sem proprietário</Badge>{" "}
            {view.last && (
              <span className="text-muted-foreground">
                Último convite: {view.last.email} ({OWNER_INVITATION_LABEL[view.last.status]})
              </span>
            )}
          </div>
          <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
            <Input
              type="email"
              aria-label="E-mail do novo proprietário"
              placeholder="E-mail do(a) proprietário(a)"
              className="w-64"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={issue.isPending}>
              Emitir novo convite
            </Button>
          </form>
          {formError && <Alert tone="error">{formError}</Alert>}
          {issue.isError && <Alert tone="error">{errorMessage(issue.error)}</Alert>}
        </div>
      )}

      {/* resultado da emissão: o link aparece só aqui, uma vez (não volta na listagem) */}
      {issue.data && (
        <Alert tone="ok">
          Novo convite para {issue.data.email}.{" "}
          {issue.data.delivery
            ? DELIVERY[issue.data.delivery]
            : "Convite já emitido nesta tentativa: o link foi mostrado na primeira resposta."}
          {issue.data.acceptUrl && (
            <span className="mt-2 flex gap-2">
              <Input readOnly value={issue.data.acceptUrl} onFocus={(e) => e.target.select()} />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void navigator.clipboard.writeText(issue.data!.acceptUrl!)}
              >
                Copiar
              </Button>
            </span>
          )}
        </Alert>
      )}
    </div>
  );
}
